import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Modal } from "../shared/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { Markdown } from "../wiki/Markdown";
import {
  FILE_ACCEPT, MIN_TEXT, MinTextCounter, Pill, SourcePill, SubjectPills, attachmentUrl, errMsg,
  fileProblem, fmtDateTime, fmtDay, fmtDue, fmtSize, ghostBtn, inputStyle, labelStyle, overdueLabel, primaryBtn,
  todaySp, uploadAttachment, type TaskAttachmentT, type TaskDetail,
} from "./common";

export function TaskModal({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: t, isLoading, error } = useQuery({
    queryKey: ["tasks", "detail", taskId],
    queryFn: () => api.get<TaskDetail>(`/tasks/${taskId}`),
  });
  const [action, setAction] = useState<"finish" | "due" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["tasks"] });
  const del = useMutation({
    mutationFn: () => api.delete(`/tasks/${taskId}`),
    onSuccess: () => { refresh(); onClose(); },
  });

  return (
    <Modal onClose={onClose} zIndex={1000}>
      <div style={{ width: 780, maxWidth: "100%", maxHeight: "calc(100vh - 48px)", overflowY: "auto", background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 12, boxShadow: "var(--shadow-modal)", padding: 24, display: "flex", flexDirection: "column", gap: 18 }}>
        {isLoading || !t ? (
          <div style={{ fontSize: 13, color: error ? "var(--danger)" : "var(--ink-muted)" }}>
            {error ? errMsg(error, "Não foi possível abrir a tarefa.") : "Carregando…"}
          </div>
        ) : (
          <>
            {/* Cabeçalho */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {t.status === "done"
                    ? <Pill color="var(--ok)" bg="var(--ok-soft)" line="var(--ok-line)">Finalizada</Pill>
                    : t.overdue
                      ? <Pill color="var(--danger)" bg="var(--danger-soft)" line="var(--danger-line)">{overdueLabel(t.due_date, t.due_time)}</Pill>
                      : <Pill>Em aberto</Pill>}
                  <SourcePill t={t} />
                  <SubjectPills subjects={t.subjects} max={10} />
                </div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--ink)", lineHeight: 1.35, wordBreak: "break-word" }}>{t.title}</h2>
              </div>
              <button onClick={onClose} aria-label="Fechar" style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 22, lineHeight: 1, cursor: "pointer" }}>×</button>
            </div>

            {/* Campos */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12, padding: 14, background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10 }}>
              <Field label="Agente" value={t.agent_name} />
              <Field label="Gestor" value={t.manager_name ?? "—"} />
              <Field label="Prazo" value={fmtDue(t.due_date, t.due_time)} color={t.overdue ? "var(--danger)" : undefined} />
              <Field label="Criada" value={`${fmtDateTime(t.created_at)} · ${t.created_by_name ?? (t.source === "api" ? "API" : "—")}`} />
              {t.postpones > 0 && <Field label="Mudanças de prazo" value={`${t.postpones}x`} color="var(--warn)" />}
              {t.finished_at && <Field label="Finalizada" value={`${fmtDateTime(t.finished_at)} · ${t.finished_by_name ?? "—"}`} color="var(--ok)" />}
            </div>

            {/* Descrição */}
            {t.description && (
              <section>
                <span style={labelStyle}>Descrição</span>
                <Markdown source={t.description} />
              </section>
            )}

            {/* Resolução */}
            {t.finish_note && (
              <section style={{ padding: 14, borderRadius: 10, background: "var(--ok-soft)", border: "1px solid var(--ok-line)" }}>
                <span style={{ ...labelStyle, color: "var(--ok)" }}>Resolução</span>
                <div style={{ fontSize: 13.5, color: "var(--ink)", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{t.finish_note}</div>
              </section>
            )}

            <Attachments task={t} onChanged={refresh} />

            {/* Histórico de prazo */}
            {t.due_changes.length > 0 && (
              <section>
                <span style={labelStyle}>Histórico de prazo</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {t.due_changes.map((c, i) => (
                    <div key={i} style={{ padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12.5 }}>
                      <div style={{ color: "var(--ink-secondary)", marginBottom: 4 }}>
                        <b style={{ color: "var(--ink)" }}>{fmtDay(c.from_date)}{c.from_time ? ` ${c.from_time}` : ""} → {fmtDay(c.to_date)}{c.to_time ? ` ${c.to_time}` : ""}</b>
                        {" · "}{c.by_name} · {fmtDateTime(c.at)}
                      </div>
                      <div style={{ color: "var(--ink)", whiteSpace: "pre-wrap" }}>{c.reason}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Ações */}
            {action === "finish" && <FinishForm taskId={t.id} onDone={() => { setAction(null); refresh(); }} onCancel={() => setAction(null)} />}
            {action === "due" && <DueForm task={t} onDone={() => { setAction(null); refresh(); }} onCancel={() => setAction(null)} />}
            {!action && (t.can_act || t.can_delete) && (
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid var(--line)", paddingTop: 16 }}>
                {t.can_delete && (
                  <button onClick={() => setConfirmDelete(true)} style={{ ...ghostBtn, color: "var(--danger)", marginRight: "auto" }}>Excluir</button>
                )}
                {t.can_act && <button onClick={() => setAction("due")} style={ghostBtn}>Mudar prazo</button>}
                {t.can_act && <button onClick={() => setAction("finish")} style={primaryBtn}>Finalizar</button>}
              </div>
            )}
            {del.isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{errMsg(del.error)}</span>}
          </>
        )}
      </div>
      {confirmDelete && (
        <ConfirmDialog
          title="Excluir tarefa?"
          message="A tarefa e todos os anexos no R2 são apagados. Não dá para desfazer."
          confirmLabel="Excluir"
          danger
          onConfirm={() => { setConfirmDelete(false); del.mutate(); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </Modal>
  );
}

function Field({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10.5, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: color ?? "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>{value}</div>
    </div>
  );
}

function FinishForm({ taskId, onDone, onCancel }: { taskId: string; onDone: () => void; onCancel: () => void }) {
  const [text, setText] = useState("");
  const mut = useMutation({ mutationFn: () => api.post(`/tasks/${taskId}/finish`, { text }), onSuccess: onDone });
  const ok = text.trim().length >= MIN_TEXT;
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
      <span style={labelStyle}>Como foi resolvida?</span>
      <textarea autoFocus rows={4} value={text} onChange={e => setText(e.target.value)} placeholder="Mínimo de 30 caracteres…" style={{ ...inputStyle, resize: "vertical" }} />
      <MinTextCounter text={text} />
      {mut.isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{errMsg(mut.error)}</span>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={ghostBtn}>Voltar</button>
        <button onClick={() => mut.mutate()} disabled={!ok || mut.isPending} style={{ ...primaryBtn, opacity: !ok || mut.isPending ? 0.6 : 1 }}>
          {mut.isPending ? "Enviando…" : "Finalizar tarefa"}
        </button>
      </div>
    </section>
  );
}

function DueForm({ task, onDone, onCancel }: { task: TaskDetail; onDone: () => void; onCancel: () => void }) {
  const [day, setDay] = useState(task.due_date < todaySp() ? todaySp() : task.due_date);
  const [time, setTime] = useState(task.due_time ?? "");
  const [reason, setReason] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/tasks/${task.id}/due`, { due_date: day, due_time: time || null, reason }),
    onSuccess: onDone,
  });
  const ok = reason.trim().length >= MIN_TEXT && !!day;
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
      <span style={labelStyle}>Novo prazo</span>
      <div style={{ display: "flex", gap: 8 }}>
        <input type="date" value={day} min={todaySp()} onChange={e => setDay(e.target.value)} style={{ ...inputStyle, width: 170 }} />
        <input type="time" value={time} onChange={e => setTime(e.target.value)} style={{ ...inputStyle, width: 120 }} aria-label="Hora (opcional)" />
        {time && <button onClick={() => setTime("")} style={{ ...ghostBtn, padding: "6px 10px" }}>Sem hora</button>}
      </div>
      <textarea autoFocus rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Por que o prazo mudou? Mínimo de 30 caracteres…" style={{ ...inputStyle, resize: "vertical" }} />
      <MinTextCounter text={reason} />
      {mut.isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{errMsg(mut.error)}</span>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={ghostBtn}>Voltar</button>
        <button onClick={() => mut.mutate()} disabled={!ok || mut.isPending} style={{ ...primaryBtn, opacity: !ok || mut.isPending ? 0.6 : 1 }}>
          {mut.isPending ? "Salvando…" : "Salvar prazo"}
        </button>
      </div>
    </section>
  );
}

function Attachments({ task, onChanged }: { task: TaskDetail; onChanged: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function send(files: FileList | null) {
    if (!files?.length) return;
    setErr(null);
    for (const f of Array.from(files)) {
      const problem = fileProblem(f);
      if (problem) { setErr(problem); continue; }
      setBusy(`Enviando ${f.name}…`);
      try { await uploadAttachment(task.id, f); } catch (e) { setErr(errMsg(e, `Falha ao enviar ${f.name}.`)); }
    }
    setBusy(null);
    if (input.current) input.current.value = "";
    onChanged();
  }

  async function remove(a: TaskAttachmentT) {
    setErr(null);
    try { await api.delete(`/tasks/${task.id}/attachments/${a.id}`); onChanged(); } catch (e) { setErr(errMsg(e)); }
  }

  if (!task.attachments.length && !task.can_attach) return null;
  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <span style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Anexos ({task.attachments.length})</span>
        {task.can_attach && (
          <>
            <input ref={input} type="file" multiple accept={FILE_ACCEPT} hidden onChange={e => send(e.target.files)} />
            <button onClick={() => input.current?.click()} disabled={!!busy} style={{ ...ghostBtn, padding: "5px 12px", fontSize: 12 }}>+ Anexar</button>
          </>
        )}
      </div>
      {busy && <div style={{ fontSize: 12, color: "var(--ink-muted)", marginBottom: 8 }}>{busy}</div>}
      {err && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 8 }}>{err}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
        {task.attachments.map(a => {
          const url = attachmentUrl(task.id, a.id);
          return (
            <div key={a.id} style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              {a.content_type.startsWith("image/") ? (
                <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={a.filename} loading="lazy" style={{ width: "100%", height: 140, objectFit: "cover", display: "block", background: "var(--surface-hover)" }} /></a>
              ) : a.content_type.startsWith("video/") ? (
                <video src={url} controls preload="metadata" style={{ width: "100%", height: 140, background: "var(--ink)", display: "block" }} />
              ) : (
                <a href={url} target="_blank" rel="noreferrer" style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface-hover)", fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)", textDecoration: "none", textTransform: "uppercase" }}>
                  {a.filename.split(".").pop()} · abrir
                </a>
              )}
              <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={url} target="_blank" rel="noreferrer" title={a.filename} style={{ fontSize: 12, color: "var(--ink)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none" }}>{a.filename}</a>
                  <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>{fmtSize(a.size)} · {a.uploaded_by_name ?? "—"}</div>
                </div>
                {a.can_delete && (
                  <button onClick={() => remove(a)} aria-label={`Remover ${a.filename}`} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 16 }}>×</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
